import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { searchRows } from '../../GscTools/components/EvidenceBlock';
import { reportsPosition } from '../../GscTools/metrics';
import { ROW_CAP } from '../../GscTools/query';
import { shortlistColumns } from '../keys';
import { MAX_SELECTED } from '../rule';

/**
 * Steps 1 and 2 of the method: the retrieved queries, and the user's selection among them.
 *
 * Selection is explicit and bounded. Nothing is evaluated because it was retrieved, nothing is
 * evaluated because it was selected — only Evaluate sends requests, one per selected query not
 * already evaluated, and never for more than the cap. "Select top" is a shortcut for the same
 * explicit act, taken by impressions because the rule's floor is in impressions.
 */
class Shortlist extends Component {
    constructor(props) {
        super(props);
        this.additionalBox = this.additionalBox.bind(this);
    }

    /** The selection's own actions beside the table, and Evaluate at the right-hand end. */
    additionalBox() {
        const { t, selected, toEvaluate, evaluating, onSelectTop, onClear, onEvaluate } =
            this.props;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <button
                        type="button"
                        className="table-additional-view-btn"
                        onClick={onSelectTop}
                    >
                        {t('tools.cannibalization.shortlist.select_top', { max: MAX_SELECTED })}
                    </button>
                    <button
                        type="button"
                        className="table-additional-view-btn"
                        onClick={onClear}
                        disabled={!selected.length}
                    >
                        {t('tools.cannibalization.shortlist.clear')}
                    </button>
                </div>

                <div className="gsc-tool-rows-meta">
                    <span className="gsc-tool-sub">
                        {t('tools.cannibalization.shortlist.count', {
                            selected: selected.length,
                            max: MAX_SELECTED,
                        })}
                    </span>
                    <button
                        type="button"
                        className="button button-filled button-primary btn d-flex align-items-center"
                        onClick={onEvaluate}
                        disabled={!toEvaluate || evaluating}
                    >
                        {t('tools.cannibalization.shortlist.evaluate', { queries: toEvaluate })}
                        {evaluating && <Loading onlyPreloader />}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const {
            t,
            scope,
            status,
            error,
            rows,
            selected,
            stopReason,
            loadingMore,
            onToggle,
            onLoadMore,
            fileName,
        } = this.props;

        if (status === 'loading') return <Loading padding={20} />;

        if (status === 'error') {
            return (
                <div className="gsc-tool-banner error">
                    {error ?? t('global.something_went_wrong')}
                </div>
            );
        }

        const columns = shortlistColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            selected,
            full: selected.length >= MAX_SELECTED,
            onToggle,
        });

        return (
            <div className="cannibalization-shortlist">
                <h5>{t('tools.cannibalization.shortlist.title')}</h5>

                {/* A failure while loading more keeps the queries already retrieved. */}
                {!!error && <div className="gsc-tool-banner error">{error}</div>}

                <DataTable
                    nodes={rows}
                    COLUMNS={columns.table}
                    customColumnsForExport={columns.shared}
                    sortFns={getSortKeys(columns.table)}
                    additionalStyles={setAdditionalStyles(columns.table)}
                    searchFunction={searchRows}
                    additionalBox={this.additionalBox}
                    fileName={fileName}
                    customEmptyMessage={t('tools.cannibalization.shortlist.empty')}
                    customContainerStyles="gsc-tool-key-table"
                    rowClassName={(item) => (selected.includes(item.key) ? 'dt-row-picked' : '')}
                    loadMore={onLoadMore}
                    loadMoreLoading={loadingMore}
                    showLoadMoreBtn={stopReason === 'more_available'}
                />

                <p className="gsc-tool-table-note">
                    {t(`tools.gsc.stop.${stopReason}`)}{' '}
                    {t('tools.cannibalization.shortlist.retrieved', {
                        rows: rows.length,
                        cap: ROW_CAP,
                    })}
                </p>
            </div>
        );
    }
}

export default withTranslation()(Shortlist);
